import { AsyncPipe, NgClass } from '@angular/common'
import type { TemplateRef } from '@angular/core'
import { Component, inject, signal, viewChild } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import type { ActivatedRouteSnapshot, ResolveFn } from '@angular/router'
import { ActivatedRoute, RouterLink } from '@angular/router'

import { TranslatePipe } from '@ngx-translate/core'
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  map,
  of,
  switchMap,
  take,
} from 'rxjs'

import { EditionsService } from '../../../api/editions-service/editions.service'
import { MagazineService } from '../../../api/magazine-service/magazine.service'
import type { EditionDto, MagazineDto } from '../../../api/responses'
import { AdvertisementComponent } from '../../../components/advertisement/advertisement.component'
import { BreadcrumbsComponent } from '../../../components/breadcrumbs/breadcrumbs.component'
import { DetailsHeaderComponent } from '../../../components/details-header/details-header.component'
import { MagazineTabsComponent } from '../../../components/magazine-tabs/magazine-tabs.component'
import { SubscribeButtonComponent } from '../../../components/subscribe-button/subscribe-button.component'
import type { SeoRouteMeta } from '../../../shared/seo/seo.types'
import { firstImage, firstText } from '../../../shared/seo/seo.util'
import type { BreadcrumbItem, YearChip } from '../../../types/types'
import { EditionComponentsModule } from './components/edition-components.module'

export interface MagazineDetailData {
  magazine: MagazineDto | null
  editions: EditionDto[]
}

const NO_MAGAZINE_DETAIL: MagazineDetailData = { magazine: null, editions: [] }

/**
 * The magazine id is the segment right after `detail`, which is what makes this
 * work for both `magazines/detail/:id[/:slug]` and its
 * `magazines/selection-essence/detail/:id[/:slug]` variant.
 */
function magazineIdFromRoute(route: ActivatedRouteSnapshot): number | null {
  const segments = route.url

  for (let i = 0; i < segments.length - 1; i++) {
    if (segments[i].path === 'detail') {
      const magazineId = parseInt(segments[i + 1].path, 10)
      if (!isNaN(magazineId)) {
        return magazineId
      }
    }
  }

  const id = parseInt(route.paramMap.get('id') ?? '', 10)
  return isNaN(id) ? null : id
}

// Fetching in a resolver (rather than the component's ngOnInit) matters for
// SSR: the router only considers navigation complete once resolvers emit, so
// this is guaranteed to be awaited before the page is rendered/captured. A
// fetch started from ngOnInit races the server's render-and-destroy cycle and
// can be torn down mid-request before it ever emits a value, which is why the
// SSR output used to show a permanent loading state for magazine pages. The
// editions are part of that same page content, so they're fetched here too
// rather than as a follow-up request from the component.
//
// Deliberately without `MagazineOptions.fetchEditions`: that nested selection
// is a trimmed-down list nothing renders, and both requests block navigation
// and the server render, so there's no point paying for a payload we throw
// away. The editions come from `getEditionsByMagazineId` below instead.
export const magazineDetailResolver: ResolveFn<MagazineDetailData> = route => {
  const magazineService = inject(MagazineService)
  const editionsService = inject(EditionsService)

  const magazineId = magazineIdFromRoute(route)
  if (magazineId === null) return of(NO_MAGAZINE_DETAIL)

  return magazineService.getMagazineById(magazineId).pipe(
    take(1),
    switchMap(magazine => {
      // Selection & Essence magazines have no editions of their own.
      if (!magazine || magazine.selectionAndEssence) {
        return of({ magazine: magazine ?? null, editions: [] })
      }

      return editionsService.getEditionsByMagazineId(magazineId).pipe(
        take(1),
        map(editions => ({ magazine, editions: editions.items })),
        catchError((error: unknown) => {
          console.error('Error fetching magazine editions:', error)
          return of({ magazine, editions: [] })
        })
      )
    }),
    catchError((error: unknown) => {
      console.error('Error fetching magazine details:', error)
      return of(NO_MAGAZINE_DETAIL)
    })
  )
}

/** The one URL to index for a magazine, out of the id-only and slug variants. */
function magazineCanonicalPath(magazine: MagazineDto): string {
  const base = magazine.selectionAndEssence
    ? '/magazines/selection-essence/detail'
    : '/magazines/detail'

  const path = `${base}/${String(magazine.id)}`

  return magazine.slug ? `${path}/${encodeURIComponent(magazine.slug)}` : path
}

/**
 * Head metadata for a magazine, from the `meta` group the API's `seoPlugin`
 * maintains. Registered as route data (`data: { seo: magazineSeo }`) so
 * `SeoTitleStrategy` can apply it as part of the navigation that resolved the
 * magazine - see seo-title.strategy.ts.
 */
export const magazineSeo: SeoRouteMeta = route => {
  const magazine = (
    route.data['magazineDetail'] as MagazineDetailData | undefined
  )?.magazine
  if (!magazine) return null

  return {
    title: firstText(magazine.meta?.title, magazine.title),
    description: firstText(
      magazine.meta?.description,
      magazine.subtitle,
      magazine.subject,
      magazine.preface
    ),
    image: firstImage(magazine.meta?.image, {
      url: magazine.media?.cover?.url,
    }),
    canonicalPath: magazineCanonicalPath(magazine),
  }
}

@Component({
  selector: 'dhp-magazines-and-journals-detail-page',
  templateUrl: './magazines-and-journals-detail-page.component.html',
  styleUrls: ['./magazines-and-journals-detail-page.component.scss'],
  imports: [
    AdvertisementComponent,
    TranslatePipe,
    RouterLink,
    SubscribeButtonComponent,
    DetailsHeaderComponent,
    BreadcrumbsComponent,
    NgClass,
    MagazineTabsComponent,
    EditionComponentsModule,
    AsyncPipe,
  ],
})
export class MagazinesAndJournalsDetailPageComponent {
  private readonly route = inject(ActivatedRoute)

  readonly magazine = signal<MagazineDto | undefined>(undefined)
  readonly loading = signal(true)
  readonly error = signal<string | null>(null)
  readonly years = signal<YearChip[]>([])
  private _isYearFilterOpen = false
  readonly breadcrumbItems = signal<BreadcrumbItem[]>([])
  readonly latestEdition = signal<EditionDto | null>(null)
  readonly hasEditions = signal(false)

  private readonly allEditions$ = new BehaviorSubject<EditionDto[]>([])
  private readonly selectedYear$ = new BehaviorSubject<YearChip | null>(null)

  editions$ = combineLatest([
    this.allEditions$,
    this.selectedYear$.pipe(
      distinctUntilChanged((prev, curr) => {
        // Compare by value not reference
        if (prev === null && curr === null) return true
        if (prev === null || curr === null) return false
        return prev.value === curr.value
      })
    ),
  ]).pipe(
    map(([editions, selectedYear]) => {
      if (!selectedYear) {
        return editions
      }

      const filteredEditions = editions.filter(
        edition => edition.year === selectedYear.value
      )

      return filteredEditions.length > 0 ? filteredEditions : editions
    })
  )

  readonly redactieContent =
    viewChild.required<TemplateRef<void>>('redactieContent')
  readonly voorWieContent =
    viewChild.required<TemplateRef<void>>('voorWieContent')
  readonly inhoudContent =
    viewChild.required<TemplateRef<void>>('inhoudContent')
  readonly pricesContent =
    viewChild.required<TemplateRef<void>>('pricesContent')

  constructor() {
    // Resolved per navigation (including a slug/id change while this component
    // is reused), and already available synchronously during SSR.
    this.route.data
      .pipe(
        map(
          data =>
            (data['magazineDetail'] as MagazineDetailData | undefined) ??
            NO_MAGAZINE_DETAIL
        ),
        takeUntilDestroyed()
      )
      .subscribe(detail => {
        this.applyMagazineDetail(detail)
      })
  }

  get isYearFilterOpen(): boolean {
    return this._isYearFilterOpen
  }

  set isYearFilterOpen(value: boolean) {
    this._isYearFilterOpen = value
  }

  get yearSelected(): YearChip | null {
    return this.selectedYear$.value
  }

  set yearSelected(value: YearChip | null) {
    this.selectedYear$.next(value)
  }

  isSelectionAndEssence(): boolean {
    return !!this.magazine()?.selectionAndEssence
  }

  private applyMagazineDetail({
    magazine,
    editions,
  }: MagazineDetailData): void {
    this.magazine.set(magazine ?? undefined)
    this.error.set(magazine ? null : 'Failed to load magazine details.')
    this.loading.set(false)

    this.allEditions$.next(editions)
    this.hasEditions.set(editions.length > 0)
    this.latestEdition.set(editions[0] ?? null)

    const years = this.generateYearsChips(editions)
    this.years.set(years)
    this.selectedYear$.next(years[0] ?? null)

    this.updateBreadcrumbs()
  }

  private generateYearsChips(editions: EditionDto[]): YearChip[] {
    const yearCounts = editions.reduce<Record<string, number>>(
      (acc, edition) => {
        const year = edition.year?.toString() ?? ''
        if (year) {
          acc[year] = (acc[year] ?? 0) + 1
        }
        return acc
      },
      {}
    )

    return Object.keys(yearCounts)
      .map(year => ({
        label: year,
        value: year ? parseInt(year, 10) : null,
        count: yearCounts[year],
      }))
      .sort((a, b) => (b.value ?? 0) - (a.value ?? 0))
  }

  private updateBreadcrumbs(): void {
    const magazine = this.magazine()
    if (!magazine) {
      this.breadcrumbItems.set([])
      return
    }

    this.breadcrumbItems.set([
      {
        label: 'components.breadcrumbs.magazines',
        url: '/magazines',
      },
      {
        label:
          magazine.abbreviation ??
          magazine.title ??
          'components.breadcrumbs.magazine',
        url: '',
      },
    ])
  }
}
